<template>
  <div class="template-list mb-5 mx-2">
    <h1 class="mb-3">Pathway Mapping Template Editor</h1>
    <div class="mb-3">
      <div class="card mb-3 table-card">
        <div class="card-header">
          <div class="row">
            <div class="col-md-6 col-12">
              <h3 class="mt-2">Templates ({{ filtered_templates.length }})</h3>
            </div>
            <div class="col-md-6 d-none d-md-block">
              <div class="d-flex justify-content-end gap-2">
                <div class="position-relative">
                  <input type="text" placeholder="Search by Source..." v-model="search_key" @input="handleFilterSelection" class="search-box form-control me-1 py-2" aria-label="Search by Source...">
                  <i class="fa fa-search search-placeholder-icon" v-show="search_key === ''"></i>
                </div>
                <a class="btn btn-primary float-end" href="/admin/pathway_mapping_templates/new">
                  <i class="fa fas fa-fw fa-plus"></i> Add Template
                </a>
              </div>
            </div>
          </div>
        </div>

        <div class="card-body p-0">
          <div class="table-width-responsive">
            <table class="table table-with-pagination mb-0">
              <thead>
                <tr class="table-header-row">
                  <th @click.prevent="sortByKey('source')" class="d-table-cell">
                    <div class="d-flex flex-row">
                      Source
                      <div class="d-flex flex-column">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{ 'sort-key-active': sort_key === 'source' && sort_order === 'asc' }" alt="">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{ 'sort-key-active': sort_key === 'source' && sort_order === 'des' }" alt="">
                      </div>
                    </div>
                  </th>
                  <th @click.prevent="sortByKey('source_subtype')" class="d-table-cell">
                    <div class="d-flex flex-row">
                      Source Subtype
                      <div class="d-flex flex-column">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{ 'sort-key-active': sort_key === 'source_subtype' && sort_order === 'asc' }" alt="">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{ 'sort-key-active': sort_key === 'source_subtype' && sort_order === 'des' }" alt="">
                      </div>
                    </div>
                  </th>
                  <th @click.prevent="sortByKey('integration_name')" class="d-none d-md-table-cell">
                    <div class="d-flex flex-row">
                      Integration
                      <div class="d-flex flex-column">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{ 'sort-key-active': sort_key === 'integration_name' && sort_order === 'asc' }" alt="">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{ 'sort-key-active': sort_key === 'integration_name' && sort_order === 'des' }" alt="">
                      </div>
                    </div>
                  </th>
                  <th @click.prevent="sortByKey('destination_object')" class="d-none d-md-table-cell">
                    <div class="d-flex flex-row">
                      Destination Object
                      <div class="d-flex flex-column">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{ 'sort-key-active': sort_key === 'destination_object' && sort_order === 'asc' }" alt="">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{ 'sort-key-active': sort_key === 'destination_object' && sort_order === 'des' }" alt="">
                      </div>
                    </div>
                  </th>
                  <th @click.prevent="sortByKey('operation')" class="d-none d-md-table-cell">
                    <div class="d-flex flex-row">
                      Operation
                      <div class="d-flex flex-column">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{ 'sort-key-active': sort_key === 'operation' && sort_order === 'asc' }" alt="">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{ 'sort-key-active': sort_key === 'operation' && sort_order === 'des' }" alt="">
                      </div>
                    </div>
                  </th>
                  <th class="text-center column-sort-disabled pe-3" width="1">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="template in list_shown" :key="template.id">
                  <td>{{ propercase(template.source) }}</td>
                  <td>{{ propercase(template.source_subtype) }}</td>
                  <td class="d-none d-md-table-cell">
                    <img :src="template.integration.logo_url" :alt="template.integration.name" class="avatar rounded-circle avatar-sm bg-white">
                    {{ propercase(template.integration_name) }}
                  </td>
                  <td class="d-none d-md-table-cell">{{ propercase(template.destination_object) }}</td>
                  <td class="d-none d-md-table-cell text-capitalize">{{ propercase(template.operation) }}</td>
                  <td class="table-column-action">
                    <div class="dropdown d-flex justify-content-center">
                      <button class="btn btn-white btn-icon-only-md" data-bs-toggle="dropdown" data-bs-auto-close="true" aria-expanded="false">
                        <img class="icon" src="/img/icons/avid-icons/dots-vertical.svg" data-bs-toggle="tooltip" title="Actions" alt="" />
                      </button>
                      <div class="dropdown-menu dropdown-menu-primary dropdown-menu-end">
                        <a class="dropdown-item" :href="`/admin/pathway_mapping_templates/${template.destination_integration_id}/${template.source}/${template.source_subtype}/edit`">
                          <img class="icon me-2" src="/img/icons/avid-icons/edit.svg" alt=""> Edit Template
                        </a>
                        <hr class="my-1" />
                        <a class="dropdown-item text-danger" href="#" @click.prevent="deleteTemplate(template)">
                          <img class="icon red-icon me-2" src="/img/icons/avid-icons/trash.svg" alt=""> Delete Template
                        </a>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr v-if="list_shown.length === 0">
                  <td colspan="5" class="text-center py-3">No templates found. <a href="/admin/pathway_mapping_templates/new">Create your first template</a>.</td>
                </tr>
              </tbody>
              <tfoot class="table-border-bottom-0">
                <tr>
                  <td colspan="100%" class="py-0">
                    <div class="table-pagination">
                      <div class="table-pagination--group d-none d-md-flex">
                        <div class="position-relative">
                          <select class="form-select" v-model="num_per_page" @change="reloadPagination(filtered_templates)" aria-label="Num Per Page">
                            <option value="5000">ALL</option>
                            <option v-for="num in num_per_page_options" :key="num" :value="num">{{ num }}</option>
                          </select>
                          <div class="select-display-text">
                            Showing {{ num_per_page === '5000' ? 'ALL' : num_per_page }}
                          </div>
                        </div>
                        <div class="position-relative">
                          <select class="form-select" v-model="num_per_page" @change="reloadPagination(filtered_templates)" aria-label="Num Per Page">
                            <option value="5000">ALL</option>
                            <option v-for="num in num_per_page_options" :key="num" :value="num">{{ num }}</option>
                          </select>
                          <div class="select-display-text">
                            Showing {{ num_per_page === '5000' ? 'ALL' : num_per_page }}
                          </div>
                        </div>
                      </div>

                      <div class="table-pagination--group d-none d-lg-flex">
                        {{ filtered_templates.length === 0 ? '0' : item_interval }} of {{ filtered_templates.length }}
                      </div>

                      <div class="table-pagination--group">
                        <button v-if="page_count > 1" class="btn btn-secondary btn-icon-only-md me-1" @click.prevent="moveToPrevPage(filtered_templates)" :disabled="page_index === 0" data-bs-toggle="tooltip" title="Previous page">
                          <img class="icon" src="/img/icons/avid-icons/chevron-left.svg" alt="">
                        </button>

                        <div class="d-flex page-selector" v-if="page_index_array.length <= 3">
                          <button v-for="page in page_index_array" :key="page" class="btn btn-none btn-icon-only-md" :class="page === (page_index + 1) ? 'current-page' : ''" @click.prevent="moveToPage(page, filtered_templates)">
                            {{ page }}
                          </button>
                        </div>
                        <div class="d-flex page-selector" v-else>
                          <button v-for="page in page_index_array.slice(0,2)" :key="page" class="btn btn-none btn-icon-only-md" :class="page === (page_index + 1) ? 'current-page' : ''" @click.prevent="moveToPage(page, filtered_templates)">
                            {{ page }}
                          </button>

                          <button v-if="page_index > 2" class="btn btn-none btn-icon-only-md text-nowrap" disabled>
                          ...
                          </button>
                          <button v-if="page_index >= 2 && page_index < page_index_array.length -1" class="btn btn-none btn-icon-only-md current-page">
                            {{ page_index + 1 }}
                          </button>
                          <button v-if="page_index < page_index_array.length -2" class="btn btn-none btn-icon-only-md text-nowrap" disabled>
                          ...
                          </button>
                          <button :class="page_index === (page_index_array.length -1) ? 'current-page' : ''" class="btn btn-none btn-icon-only-md" @click.prevent="moveToPage(page_index_array.length, filtered_templates)" :key="page_index_array.length">
                            {{ page_index_array.length }}
                          </button>
                        </div>

                        <button v-if="page_count > 1" class="btn btn-secondary btn-icon-only-md ms-1" @click.prevent="moveToNextPage(filtered_templates)" :disabled="page_index === page_count -1" data-bs-toggle="tooltip" title="Next page">
                          <img class="icon" src="/img/icons/avid-icons/chevron-right.svg" alt="">
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import tablePaginationMixin from '../../mixins/table_pagination';
import Swal from 'sweetalert2';
import { useNotifications } from '../../composables/useNotifications';

export default {
  name: 'PathwayMappingTemplateList',
  props: {
    templates: {
      type: Array,
      default: () => []
    }
  },
  setup() {
    const { showNotification } = useNotifications();
    return {
      showNotification
    };
  },
  mixins: [tablePaginationMixin],
  data() {
    return {
      local_templates: [],
      filtered_templates: [],
      list_shown: [],
      search_key: '',
      sort_key: 'source',
      sort_order: 'asc'
    };
  },
  beforeMount() {
    this.local_templates = this.templates.map(t => ({
      ...t,
      integration_name: t.integration ? t.integration.display_name : '',
    }));
    this.filtered_templates = [...this.local_templates];
    this.reloadPagination(this.filtered_templates);
  },
  methods: {
    handleFilterSelection() {
      const term = this.search_key.trim().toLowerCase();
      this.filtered_templates = this.local_templates.filter(t => {
        if (!term) return true;
        return t.source.toLowerCase().includes(term) ||
               t.source_subtype.toLowerCase().includes(term) ||
               (t.destination_object || '').toLowerCase().includes(term) ||
               (t.integration_name || '').toLowerCase().includes(term);
      });
      this.sortData();
    },
    sortData() {
      this.filtered_templates.sort((a, b) => {
        const key = this.sort_key;
        if (a[key] < b[key]) return this.sort_order === 'asc' ? -1 : 1;
        if (a[key] > b[key]) return this.sort_order === 'asc' ? 1 : -1;
        return 0;
      });
      this.reloadPagination(this.filtered_templates);
    },
    sortByKey(key) {
      if (this.sort_key === key) {
        this.sort_order = this.sort_order === 'asc' ? 'des' : 'asc';
      } else {
        this.sort_key = key;
        this.sort_order = 'asc';
      }
      this.sortData();
    },
    async deleteTemplate(template) {
      // Remove from local_templates as well
      const result = await Swal.fire({
        title: 'Are you sure you want to delete this template?',
        text: 'This action cannot be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it',
        customClass: {
          popup: 'swal-warning'
        }
      });

      if (result.isConfirmed) {
        try {
          await window.axios.delete(`/api/pathway_mapping_templates/${template.id}`);

          this.local_templates = this.local_templates.filter(t => t.id !== template.id);
          this.filtered_templates = this.filtered_templates.filter(t => t.id !== template.id);
          this.reloadPagination(this.filtered_templates);

          this.showNotification({
            text: 'Your template was deleted!'
          });
        } catch (error) {
          await Swal.fire({
            title: 'Error',
            text: 'There was an error deleting the template. Please try again later.',
            icon: 'error',
            confirmButtonText: 'OK',
            customClass: {
              popup: 'swal-error'
            }
          });
        }
      }
    }
  }
};
</script>

<style scoped>
/* Reuse styles from PageList if needed */
</style> 